"use client";

import { useState } from "react";
import { cn } from "@/app/lib/utils/cn";
import { categories, questionsByCategory } from "@/app/lib/data/faqData";

const tabColors = ["gold", "pink", "purple", "gold", "pink"] as const;
type TabColor = typeof tabColors[number];

const accentClasses: Record<TabColor, string> = {
  gold: "text-(--gold)",
  pink: "text-(--brand)",
  purple: "text-(--purple-accent)",
};

const bgClasses: Record<TabColor, string> = {
  gold: "bg-(--gold)",
  pink: "bg-(--brand)",
  purple: "bg-(--purple)",
};

const borderClasses: Record<TabColor, string> = {
  gold: "border-(--gold)",
  pink: "border-(--brand)",
  purple: "border-(--purple-accent)",
};

const glowClasses: Record<TabColor, string> = {
  gold: "shadow-[0_0_20px_rgba(255,204,1,0.3)]",
  pink: "shadow-[0_0_20px_rgba(232,119,216,0.3)]",
  purple: "shadow-[0_0_20px_var(--purple-accent-glow)]",
};

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  color: TabColor;
}

function FAQItem({ question, answer, isOpen, onToggle, color }: FAQItemProps) {
  return (
    <div className={cn(
      "border-b border-(--border-color) last:border-b-0 rounded-lg transition-all duration-200",
      isOpen && "bg-(--bg-secondary)"
    )}>
      <button
        onClick={onToggle}
        className="w-full py-4 px-4 flex items-center justify-between text-left hover:bg-(--bg-secondary) transition-colors rounded-lg group"
        aria-expanded={isOpen}
      >
        <span className={cn(
          "font-['Playfair_Display'] font-semibold text-lg md:text-xl pr-4 transition-colors",
          isOpen ? accentClasses[color] : "text-(--text-secondary) group-hover:text-(--text-primary)"
        )}>
          {question}
        </span>
        <span
          className={cn(
            "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300",
            isOpen 
              ? cn(bgClasses[color], "rotate-180") 
              : "bg-(--purple)/20 group-hover:bg-(--purple)/40"
          )}
        >
          <svg
            className={cn(
              "w-5 h-5 transition-colors",
              isOpen 
                ? (color === "gold" ? "text-(--text-on-gold)" : color === "pink" ? "text-(--text-on-pink)" : "text-(--text-on-purple)")
                : "text-(--purple)"
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className={cn(
          "px-4 pb-4 pt-4 text-(--text-muted) font-['Poppins'] leading-relaxed",
          "border-l-4 ml-4 pl-4",
          borderClasses[color]
        )}>
          {answer}
        </div>
      </div>
    </div>
  );
}

export default function FAQTabs() {
  const [activeCategory, setActiveCategory] = useState("general");
  const [openIndices, setOpenIndices] = useState<number[]>([]);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    setOpenIndices([]);
  };

  const handleToggle = (index: number) => {
    setOpenIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const currentQuestions = questionsByCategory[activeCategory] ?? [];
  const activeIndex = categories.findIndex(c => c.id === activeCategory);
  const activeColor = tabColors[activeIndex] || "pink";
  const currentCategory = categories.find(c => c.id === activeCategory);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-10">
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
          {categories.map((category, index) => {
            const isActive = activeCategory === category.id;
            const color = tabColors[index]!;
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={cn(
                  "group relative px-4 py-3 sm:px-6 sm:py-4 rounded-xl font-['Font-Bold'] text-sm sm:text-base transition-all duration-300",
                  "border-2 flex items-center gap-2",
                  isActive
                    ? cn(
                        bgClasses[color],
                        borderClasses[color],
                        glowClasses[color],
                        "scale-105",
                        color === "gold" ? "text-(--text-on-gold)" : color === "pink" ? "text-(--text-on-pink)" : "text-(--text-on-purple)"
                      )
                    : cn(
                        "bg-(--bg-primary) border-(--border-color)",
                        "hover:border-(--purple) hover:bg-(--purple)/10",
                        "text-(--text-secondary) hover:text-(--text-primary)"
                      )
                )}
              >
                <span className="text-lg sm:text-xl" role="img" aria-hidden="true">
                  {category.icon}
                </span>
                <span className="hidden sm:inline">{category.label}</span>
                <span className="sm:hidden">{category.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={cn(
        "relative rounded-2xl p-1 transition-all duration-300",
        glowClasses[activeColor]
      )}>
        <div className={cn(
          "absolute inset-0 rounded-2xl opacity-20",
          bgClasses[activeColor]
        )} />
        <div className="relative theme-card rounded-2xl p-6 sm:p-8 overflow-hidden">
          <div className={cn(
            "absolute top-0 left-0 w-full h-1",
            bgClasses[activeColor]
          )} />
          
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="text-3xl sm:text-4xl" role="img" aria-hidden="true">
              {currentCategory?.icon}
            </span>
            <h2 className="font-['Font-Bold'] text-2xl sm:text-3xl">
              <span className={accentClasses[activeColor]}>
                {currentCategory?.label.split(" ")[0]}
              </span>{" "}
              <span className="text-(--text-primary)">
                {currentCategory?.label.split(" ").slice(1).join(" ") || "Questions"}
              </span>
            </h2>
          </div>
          
          <div className="space-y-1">
            {currentQuestions.map((item, index) => (
              <FAQItem
                key={`${activeCategory}-${index}`}
                question={item.question}
                answer={item.answer}
                isOpen={openIndices.includes(index)}
                onToggle={() => handleToggle(index)}
                color={activeColor}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap justify-center gap-4">
        {categories.map((category, index) => {
          const isActive = activeCategory === category.id;
          const color = tabColors[index]!;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={cn(
                "flex items-center gap-1.5 text-sm transition-all duration-200",
                isActive
                  ? cn(accentClasses[color], "font-semibold scale-105")
                  : "text-(--text-muted) hover:text-(--text-secondary)"
              )}
            >
              <span className="text-base">{category.icon}</span>
              <span>{category.shortLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
